import { translateError } from '@/common/helpers/error-translation.helper';
import { UrlService } from '@/common/url/url.service';
import { GtmError } from '@/core/domain/gtm/models/gtm-error.models';
import { GtmReedemCode } from '@/core/domain/gtm/models/gtm-reedem-code.models';
import { SetDataLayerParamsUseCase } from '@/core/domain/gtm/usecases/setDataLayer.usecase';
import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import {
    LoderCardRedeem,
    PostCardRedeemB2BUseCase,
    PostCardRedeemMPUseCase,
    PostCardRedeemUseCase,
} from '../../../../core/domain/card-redeem/usecases/postCardRedeem-loader.usecase';
import {
    ErrorCode,
    REDEEMED_CARD_ERROR_CODE,
    getRedeemErrorTranslationCode,
    isRetryableRedeemError,
    setErrorCodes,
} from './error-codes';
@Component({
    selector: 'vg-card-redeem',
    templateUrl: './card-redeem.component.html',
    styleUrls: ['./card-redeem.component.scss'],
})
export class CardRedeemComponent {
    public cardRedeemForm: FormGroup;
    @Input()
    title = '';
    @Input()
    subtitle = '';
    @Input()
    btnTitle = '';
    @Input()
    image = '';
    @Input()
    btnCompensations = false;
    errorCodes = setErrorCodes(this.translate);
    errorRedeemValues: ErrorCode = { title: '', text: '', btnTitle: '' };
    enabledBtnRedeem: boolean;
    regexCardNumber: string;
    modalError: boolean;
    modalSuccess: boolean;
    isLoadingBtn: boolean;
    codeCard: string;
    titleError: string;
    textError: string;
    btnTitleError: string;
    isCompensation: boolean;
    expirationDateFormattedSuccess: string;
    emailInvalidRecipent = '';
    public urlRedirect = 'dashboard/home';
    private retryableRedeemCardId = '';
    constructor(
        private translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        private readonly postCardRedeemUseCase: PostCardRedeemUseCase,
        private readonly postCardRedeemB2BUseCase: PostCardRedeemB2BUseCase,
        private readonly postCardRedeemMPUseCase: PostCardRedeemMPUseCase,
        private readonly gtmDataLayer: SetDataLayerParamsUseCase,
        private readonly url: UrlService,
        private readonly router: Router,
    ) {
        this.enabledBtnRedeem = false;
        this.regexCardNumber = '/^[A-Z0-9]{15}$/';
        this.modalError = false;
        this.modalSuccess = false;
        this.isLoadingBtn = false;
        this.codeCard = '';
        this.titleError = '';
        this.textError = '';
        this.btnTitleError = '';
        this.isCompensation = false;
        this.expirationDateFormattedSuccess = '';
        this.cardRedeemForm = this.formBuilder.group({
            cardNumber: [
                '',
                Validators.compose([
                    Validators.required,
                    Validators.pattern('^[A-Z0-9]{14,15}$'),
                ]),
            ],
        });
    }

    receiveClose($event: boolean) {
        this.modalError = $event;
    }

    receiveCloseSuccess($event: boolean) {
        if ($event === true) {
            window.open(
                this.translate.instant(
                    'vg_landing_dashboard_url_payment-methods',
                ),
                '_blank',
            );
        }
        if ($event === false) {
            this.modalSuccess = false;
            this.url
                .format(this.urlRedirect)
                .subscribe((url) => this.router.navigateByUrl(url));
        }
    }

    onChangeCardNumber(event: any) {
        let inputValue: string = event.target.value;
        inputValue = inputValue.replace(/-/g, '');
        inputValue = inputValue.toUpperCase();

        this.cardRedeemForm.patchValue(
            { cardNumber: inputValue },
            { emitEvent: false },
        );

        const cardNumberControl = this.cardRedeemForm.get('cardNumber');
        if (
            cardNumberControl &&
            cardNumberControl.valid &&
            !this.isLoadingBtn
        ) {
            this.enabledBtnRedeem = true;
        } else {
            this.enabledBtnRedeem = false;
        }
    }

    async onClickRedeem() {
        if (this.enabledBtnRedeem) {
            this.enabledBtnRedeem = false;
            this.isLoadingBtn = true;

            const cardNumberControlValue =
                this.cardRedeemForm.get('cardNumber')?.value;

            try {
                await this.submitRedeemCard({ cardId: cardNumberControlValue });
            } finally {
                this.isLoadingBtn = false;
                const cardNumberControl = this.cardRedeemForm.get('cardNumber');
                this.enabledBtnRedeem = !!(
                    cardNumberControl && cardNumberControl.valid
                );
            }
        }
    }

    formatExpirationDate(expirationDate: string): string {
        const date = new Date(expirationDate);
        const options: Intl.DateTimeFormatOptions = {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        };
        return date.toLocaleDateString('es-ES', options);
    }

    private async submitRedeemCard(cardRedeem: LoderCardRedeem): Promise<void> {
        const isB2B = cardRedeem.cardId.startsWith('BG');
        const isMP = cardRedeem.cardId.startsWith('MP');
        const isSR = cardRedeem.cardId.startsWith('SR');
        const isCG = cardRedeem.cardId.startsWith('CG');

        try {
            if (isB2B) {
                await firstValueFrom(
                    this.postCardRedeemB2BUseCase.execute(cardRedeem),
                ).then((data) => this.handleResponse(data, cardRedeem.cardId));
            } else if (isMP || isSR || isCG) {
                await firstValueFrom(
                    this.postCardRedeemMPUseCase.execute(cardRedeem),
                ).then((data) => this.handleResponse(data, cardRedeem.cardId));
            } else {
                await firstValueFrom(
                    this.postCardRedeemUseCase.execute(cardRedeem),
                ).then((data) => this.handleResponse(data, cardRedeem.cardId));
            }
        } catch (error) {
            this.setErrorCode();
            this.modalError = true;
            this.gtmErrorAction();
        }
    }

    private handleResponse(data: any, cardId: string) {
        if (data.error) {
            const errorCode = data.error.code;

            if (this.isRedeemedAfterRetry(errorCode, cardId)) {
                this.showSuccessModal(data, this.isCompensationCard(cardId));
                this.gtmAction('successful', '', '', '');
                return;
            }

            if (isRetryableRedeemError(errorCode)) {
                this.retryableRedeemCardId = cardId;
            } else {
                this.retryableRedeemCardId = '';
            }

            if (errorCode === 'INVALID_RECIPIENT') {
                this.emailInvalidRecipent = data.error.data.email;
            }
            this.setErrorCode(errorCode);
            this.modalError = true;
            this.gtmErrorAction();
            this.gtmError(data.error.type, errorCode, data.error.message);
        } else if (data.type === 'SUCCESS' && data.action === 'CONTINUE') {
            this.showSuccessModal(data, this.isCompensationCard(cardId));
            this.gtmAction(
                'successful',
                data.data.cardStatus,
                data.data.expirationDate,
                data.data.amount,
            );
        } else {
            this.setErrorCode();
            this.modalError = true;
            this.gtmErrorAction();
            this.gtmError(data.error.type, data.error.code, data.error.message);
        }
    }

    private setErrorCode(errorCode = 'DEFAULT_ERROR'): void {
        const translationCode = getRedeemErrorTranslationCode(errorCode);
        this.errorRedeemValues = translateError(
            this.translate,
            translationCode,
            this.emailInvalidRecipent,
        );

        if (
            isRetryableRedeemError(errorCode) &&
            this.errorRedeemValues.title ===
                this.translate.instant(
                    'vg_errors_code_GENERIC_VALIDATION_ERROR_title',
                )
        ) {
            this.errorRedeemValues = translateError(
                this.translate,
                'DEFAULT_ERROR',
                this.emailInvalidRecipent,
            );
        }
    }

    private isRedeemedAfterRetry(errorCode: string, cardId: string): boolean {
        return (
            errorCode === REDEEMED_CARD_ERROR_CODE &&
            this.retryableRedeemCardId === cardId
        );
    }

    private isCompensationCard(cardId: string): boolean {
        return ['CG', 'MP', 'SR'].some((prefix) => cardId.startsWith(prefix));
    }

    private showSuccessModal(data: any, isCompensation: boolean): void {
        this.retryableRedeemCardId = '';
        if (data.data?.expirationDate) {
            this.expirationDateFormattedSuccess = this.formatExpirationDate(
                data.data.expirationDate,
            );
        }
        this.isCompensation = isCompensation;
        this.modalSuccess = true;
        this.urlRedirect = this.isCompensation
            ? 'dashboard/compensations'
            : 'dashboard/gifts';
    }

    gtmErrorAction() {
        this.gtmAction('unsuccessful', '', '', '');
    }

    gtmAction(
        label: string,
        status: string,
        expirationDate: string,
        amount: string,
    ) {
        this.gtmDataLayer.execute<GtmReedemCode>({
            event: 'ev_viva_gift_redeem_card',
            category: 'viva_gift',
            action: 'redeem_card',
            label: label,
            viva_gift: {
                status: status,
                expiration_date:
                    expirationDate === ''
                        ? ''
                        : DateTime.fromISO(expirationDate).toFormat(
                              'dd/MM/yyyy',
                          ), // 01/01/2023
                amount: '' + amount,
            },
        });
    }

    gtmError(type: string, code: string, message: string) {
        let translatedCode = this.translate.instant(
            'vg_errors_code_' + code + '_text',
        );

        if (code === 'INVALID_RECIPIENT') {
            translatedCode = translatedCode.replace(
                '${email}',
                this.emailInvalidRecipent,
            );
        }
        this.gtmDataLayer.execute<GtmError>({
            event: 'ev_message',
            message: {
                type: `${type} | ${translatedCode}`,
                code: code,
                message: message,
            },
        });
    }
}
